import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { Modal, useToast } from "@/components/common";
import styles from "./UploadDocumentsModal.module.css";
const DEFAULT_CATEGORIES = ["Contract", "Quote", "Invoice", "Job", "Other"];
const ALLOWED_MIME_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv",
    "application/zip",
    "application/x-zip-compressed",
];
// MIME type normalization map for browser inconsistencies
const MIME_TYPE_NORMALIZATION = {
    "text/comma-separated-values": "text/csv",
    "application/csv": "text/csv",
    "text/x-csv": "text/csv",
    "application/x-csv": "text/csv",
    "image/jpg": "image/jpeg", // Normalize jpg to jpeg
};
// File extension to MIME type mapping for fallback detection
const EXTENSION_TO_MIME = {
    ".pdf": "application/pdf",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".xls": "application/vnd.ms-excel",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".txt": "text/plain",
    ".csv": "text/csv",
    ".zip": "application/zip",
};
/**
 * Gets MIME type from file extension as fallback
 */
const getMimeTypeFromExtension = (fileName) => {
    const extension = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
    return EXTENSION_TO_MIME[extension] || null;
};
/**
 * Normalizes MIME types to match backend expectations
 * Handles browser inconsistencies (e.g., CSV files can have various MIME types)
 * Falls back to extension-based detection if MIME type is missing or empty
 */
const normalizeMimeType = (file) => {
    let mimeType = file.type;
    // If MIME type is missing or empty, try to detect from extension
    if (!mimeType || mimeType === "application/octet-stream") {
        const detectedType = getMimeTypeFromExtension(file.name);
        if (detectedType) {
            mimeType = detectedType;
        }
    }
    // Normalize known variations
    const normalizedType = MIME_TYPE_NORMALIZATION[mimeType] || mimeType;
    // If the type was normalized or detected, create a new File object with the corrected type
    if (normalizedType !== file.type) {
        return new File([file], file.name, {
            type: normalizedType,
            lastModified: file.lastModified,
        });
    }
    return file;
};
const isValidFileType = (file) => {
    const normalizedFile = normalizeMimeType(file);
    return ALLOWED_MIME_TYPES.includes(normalizedFile.type);
};
export const UploadDocumentsModal = ({ isOpen, onClose, onSave, categories = DEFAULT_CATEGORIES, defaultCategory, categoryLabel = "CATEGORY", isUploading = false, uploadButtonText = "Uploading...", }) => {
    const { error: showError } = useToast();
    const [selectedCategory, setSelectedCategory] = useState("Select Category");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [urlInput, setUrlInput] = useState("");
    const fileInputRef = useRef(null);
    const fileIdCounter = useRef(1);
    useEffect(() => {
        if (isOpen && defaultCategory && categories.includes(defaultCategory)) {
            setSelectedCategory(defaultCategory);
        }
    }, [isOpen, defaultCategory, categories]);
    const handleClose = () => {
        if (isUploading)
            return; // Prevent closing during upload
        setSelectedCategory("Select Category");
        setIsDropdownOpen(false);
        setUploadedFiles([]);
        setUrlInput("");
        fileIdCounter.current = 1;
        onClose();
    };
    const handleSave = async () => {
        if (uploadedFiles.length > 0 && selectedCategory !== "Select Category") {
            try {
                await onSave(uploadedFiles, selectedCategory);
                handleClose();
            }
            catch {
                // Do not close — keep modal open with category and files so user can retry
            }
        }
    };
    const formatFileSize = (bytes) => {
        const sizes = ["B", "KB", "MB", "GB"];
        let order = 0;
        let size = bytes;
        while (size >= 1024 && order < sizes.length - 1) {
            order++;
            size /= 1024;
        }
        return `${size.toFixed(2)} ${sizes[order]}`;
    };
    const simulateUpload = async (file) => {
        for (let progress = 0; progress <= 100; progress += 10) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            setUploadedFiles((prev) => prev.map((f) => f.id === file.id
                ? {
                    ...f,
                    progress,
                    isUploading: progress < 100,
                    timeRemaining: progress < 100
                        ? `${((100 - progress) / 10) * 5} sec left`
                        : undefined,
                }
                : f));
        }
    };
    const handleFileInput = (event) => {
        const files = event.target.files;
        if (files) {
            processFiles(Array.from(files));
        }
    };
    const processFiles = (files) => {
        const validFiles = [];
        const invalidFiles = [];
        files.forEach((file) => {
            // Normalize MIME type before validation
            const normalizedFile = normalizeMimeType(file);
            if (isValidFileType(normalizedFile)) {
                validFiles.push(normalizedFile);
            }
            else {
                invalidFiles.push(file.name);
            }
        });
        if (invalidFiles.length > 0) {
            showError(`The following file(s) are not supported: ${invalidFiles.join(", ")}. Please upload PDF, images (JPEG, PNG, GIF, WebP), Word documents, Excel files, text files, CSV files, or ZIP archives.`);
        }
        validFiles.forEach((file) => {
            const uploadedFile = {
                id: fileIdCounter.current++,
                name: file.name,
                size: formatFileSize(file.size),
                progress: 0,
                isUploading: true,
                timeRemaining: "Uploading...",
                file: file, // Store the normalized File object with correct MIME type
            };
            setUploadedFiles((prev) => [...prev, uploadedFile]);
            simulateUpload(uploadedFile);
        });
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };
    const handleDragLeave = () => {
        setIsDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };
    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };
    const handleAddFromUrl = async () => {
        if (urlInput.trim()) {
            try {
                // Fetch the file from URL
                const response = await fetch(urlInput.trim());
                if (!response.ok) {
                    showError("Failed to fetch file from URL");
                    return;
                }
                const blob = await response.blob();
                const fileName = urlInput.split("/").pop()?.split("?")[0] ||
                    `Document_${fileIdCounter.current}.pdf`;
                // Create a File object from the blob
                let file = new File([blob], fileName, { type: blob.type });
                // Normalize MIME type before validation
                file = normalizeMimeType(file);
                // Validate file type
                if (!isValidFileType(file)) {
                    showError(`File type "${blob.type}" is not supported. Please upload PDF, images (JPEG, PNG, GIF, WebP), Word documents, Excel files, text files, CSV files, or ZIP archives.`);
                    return;
                }
                const uploadedFile = {
                    id: fileIdCounter.current++,
                    name: fileName,
                    size: formatFileSize(file.size),
                    progress: 100,
                    isUploading: false,
                    file: file, // Store the normalized File object with correct MIME type
                };
                setUploadedFiles((prev) => [...prev, uploadedFile]);
                setUrlInput("");
            }
            catch (error) {
                console.error("Error fetching file from URL:", error);
                showError("Failed to fetch file from URL");
            }
        }
    };
    const removeFile = (fileId) => {
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: handleClose, children: _jsxs("div", { className: styles.modalWrapper, children: [_jsx("div", { className: styles.modalHeader, children: _jsx("h2", { className: styles.modalTitle, children: "Upload Document" }) }), _jsxs("div", { className: styles.modalBody, children: [_jsxs("div", { className: styles.modalLeft, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: styles.formLabel, children: categoryLabel }), _jsxs("div", { className: styles.dropdownContainer, children: [_jsxs("button", { type: "button", className: styles.dropdownToggle, onClick: () => setIsDropdownOpen(!isDropdownOpen), children: [_jsx("span", { className: `${styles.dropdownText} ${selectedCategory === "Select Category"
                                                                ? styles.placeholder
                                                                : ""}`, children: selectedCategory }), _jsx("svg", { className: `${styles.dropdownArrow} ${isDropdownOpen ? styles.open : ""}`, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M4 6L8 10L12 6", stroke: "#2563EB", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })] }), isDropdownOpen && (_jsx("div", { className: styles.dropdownMenu, children: categories.map((category) => (_jsx("button", { type: "button", className: styles.dropdownItem, onClick: () => {
                                                            setSelectedCategory(category);
                                                            setIsDropdownOpen(false);
                                                        }, children: category }, category))) }))] })] }), _jsxs("div", { className: `${styles.uploadArea} ${isDragOver ? styles.dragOver : ""}`, onClick: triggerFileInput, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [_jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: ".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip", onChange: handleFileInput, style: { display: "none" } }), _jsx("div", { className: styles.uploadIcon, children: _jsx("span", { className: `material-icons ${styles.bigIcon}`, "aria-hidden": "true", children: "note_add" }) }), _jsx("h3", { className: styles.uploadTitle, children: "Browse from your computer" }), _jsx("p", { className: styles.uploadSubtitle, children: "or Drag & Drop" })] }), _jsxs("div", { className: styles.urlSection, children: [_jsx("p", { className: styles.urlLabel, children: "or Upload file from URL" }), _jsxs("div", { className: styles.urlInputGroup, children: [_jsx("input", { type: "text", className: styles.urlInput, placeholder: "Add the file URL", value: urlInput, onChange: (e) => setUrlInput(e.target.value) }), _jsx("button", { type: "button", className: styles.urlUploadBtn, onClick: handleAddFromUrl, children: "UPLOAD" })] })] })] }), _jsx("div", { className: styles.modalRight, children: uploadedFiles.length === 0 ? (_jsx("div", { className: styles.emptyState, children: _jsxs("div", { className: styles.emptyContent, children: [_jsx("div", { className: styles.emptyIcon, children: _jsx("span", { className: `material-icons ${styles.bigIcon}`, "aria-hidden": "true", children: "copy_all" }) }), _jsxs("div", { style: { textAlign: "left" }, children: [_jsx("h3", { className: styles.uploadTitle, children: "Your document space is empty." }), _jsx("p", { className: styles.uploadSubtitle, children: "Drag & drop files or click \"Upload\" to get started." })] })] }) })) : (_jsxs("div", { className: styles.filesListContainer, children: [_jsxs("h3", { className: styles.filesHeader, children: ["UPLOADING ", uploadedFiles.length, "/", uploadedFiles.length, " FILES"] }), _jsx("div", { className: styles.filesList, children: uploadedFiles.map((file) => (_jsxs("div", { className: `${styles.fileItem} ${file.isUploading ? styles.uploading : styles.completed}`, children: [_jsx("div", { className: styles.fileIcon, children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z", fill: "#2563EB", stroke: "#2563EB", strokeWidth: "1" }), _jsx("path", { d: "M14 2v6h6", stroke: "#fff", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("text", { x: "12", y: "17", textAnchor: "middle", fill: "#fff", fontSize: "7", fontWeight: "600", children: "PDF" })] }) }), _jsxs("div", { className: styles.fileDetails, children: [_jsx("div", { className: styles.fileName, children: file.name }), _jsx("div", { className: styles.fileStatus, children: file.isUploading ? (_jsxs("span", { children: [file.progress, "% | ", file.timeRemaining] })) : (_jsxs("span", { children: [file.progress, "% | ", file.size] })) })] }), _jsxs("div", { className: styles.fileActions, children: [_jsx("button", { type: "button", className: `${styles.fileActionBtn} ${styles.deleteBtn}`, onClick: () => removeFile(file.id), children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", children: [_jsx("path", { d: "M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6", stroke: "#EF4444", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M10 11v6M14 11v6", stroke: "#EF4444", strokeWidth: "2", strokeLinecap: "round" })] }) }), file.isUploading ? (_jsx("button", { type: "button", className: `${styles.fileActionBtn} ${styles.pauseBtn}`, children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", children: [_jsx("circle", { cx: "12", cy: "12", r: "10", stroke: "#FF9800", strokeWidth: "2" }), _jsx("rect", { x: "9", y: "8", width: "2", height: "8", fill: "#FF9800" }), _jsx("rect", { x: "13", y: "8", width: "2", height: "8", fill: "#FF9800" })] }) })) : (_jsx("button", { type: "button", className: `${styles.fileActionBtn} ${styles.checkBtn}`, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M20 6L9 17l-5-5", stroke: "#10B981", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }))] })] }, file.id))) })] })) })] }), _jsxs("div", { className: styles.modalFooter, children: [_jsx("button", { type: "button", className: styles.cancelBtn, onClick: handleClose, disabled: isUploading, children: "Cancel" }), _jsx("button", { type: "button", className: styles.saveBtn, onClick: handleSave, disabled: uploadedFiles.length === 0 ||
                                selectedCategory === "Select Category" ||
                                isUploading, children: isUploading ? uploadButtonText : "Save Documents" })] })] }) }));
};
